import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import moment from 'moment';
import { useNavigate, useParams } from 'react-router-dom';
import { CompanyApi } from '../../../../../api/companyApi';
import {
  errorToast,
  successToast,
  getLocalStorageItem,
  isCopanySubAdmin,
  isFutureDate,
  minFutureDateString
} from '../../../../../utils/helper';
import Loader from '../../../../../component/common/Loader';
import Autocomplete from '../../../../../component/common/Autocomplete';
import Topbar from '../../../../PulseSurvey/Topbar';
import Breadcrumb from '../../../../../component/common/Breadcrumb';
import CommentsPanel from '../components/CommentsPanel';
import {
  StandardButton,
  StandardDropdown,
  FIELD_CLASS
} from '../../../../../component/common/StandardUI';
import ConfirmPopup from '../../../../../component/common/modals/ConfirmPopup';
import { RecycleBinIcon } from '../components/icons';
import ChecklistCheckbox from '../components/ChecklistCheckbox';
import { getOwnerRoleLabel } from '../components/ownerRole';
import checklistIcon from '../../../../../assets/svg/icons/List-To-Do-Tasks-Checklist--Streamline-Flex.svg';
import targetIcon from '../../../../../assets/svg/icons/Target--Streamline-Flex.svg';
import alarmClockIcon from '../../../../../assets/svg/icons/Alarm-Clock--Streamline-Flex.svg';
import pencilIcon from '../../../../../assets/svg/icons/Pencil--Streamline-Flex.svg';

const ViewGoal = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const userData = JSON.parse(getLocalStorageItem('userData'));
  const slug = userData?.slug;
  const isSubAdmin = isCopanySubAdmin();

  const [goal, setGoal] = useState(null);
  const [loading, setLoading] = useState(false);
  const [togglingId, setTogglingId] = useState(null);
  const [commentSubmitting, setCommentSubmitting] = useState(false);
  const [addingOwner, setAddingOwner] = useState(false);
  const [ownerOptions, setOwnerOptions] = useState([]);
  const [ownerLoading, setOwnerLoading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [editingDeadline, setEditingDeadline] = useState(false);
  const [deadlineDraft, setDeadlineDraft] = useState('');
  const [deadlineError, setDeadlineError] = useState('');
  const [savingDeadline, setSavingDeadline] = useState(false);

  const fetchGoal = async () => {
    try {
      setLoading(true);
      const response = await CompanyApi.getGoalById(id);
      if (response?.data?.meta?.code === 1) {
        setGoal(response?.data?.data);
      } else {
        errorToast(response?.data?.meta?.message || 'Failed to load goal');
      }
    } catch (error) {
      errorToast(error.message || 'Failed to load goal');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGoal();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleToggleChecklist = async (checklistItemId) => {
    try {
      setTogglingId(checklistItemId);
      const response = await CompanyApi.toggleGoalChecklistItem(id, checklistItemId);
      if (response?.data?.meta?.code === 1) {
        fetchGoal();
      } else {
        errorToast(response?.data?.meta?.message || 'Failed to update checklist item');
      }
    } catch (error) {
      errorToast(error.message || 'Failed to update checklist item');
    } finally {
      setTogglingId(null);
    }
  };

  const searchOwners = async (searchKey) => {
    try {
      setOwnerLoading(true);
      const response = await CompanyApi.getPeoplePlanOwners(searchKey);
      if (response?.data?.meta?.code === 1) {
        setOwnerOptions(response?.data?.data || []);
      }
    } catch (error) {
      errorToast(error.message || 'Failed to search admins');
    } finally {
      setOwnerLoading(false);
    }
  };

  const handleAddOwner = async (user) => {
    const existingIds = (goal.assignedOwner || []).map((o) => o._id || o);
    if (existingIds.includes(user._id)) {
      setAddingOwner(false);
      return;
    }
    try {
      setLoading(true);
      const response = await CompanyApi.updateGoal(id, {
        title: goal.title,
        targetMetric: goal.targetMetric,
        endDate: goal.endDate,
        assignedOwner: [...existingIds, user._id],
        linkedMilestones: (goal.linkedMilestones || []).map((m) => m._id || m),
        checklist: (goal.checklist || []).map((item) => ({
          text: item.text,
          isCompleted: !!item.isCompleted
        }))
      });
      if (response?.data?.meta?.code === 1) {
        successToast('Owner added');
        setAddingOwner(false);
        fetchGoal();
      } else {
        errorToast(response?.data?.meta?.message || 'Failed to add owner');
      }
    } catch (error) {
      errorToast(error.message || 'Failed to add owner');
    } finally {
      setLoading(false);
    }
  };

  const handleAddComment = async (text) => {
    try {
      setCommentSubmitting(true);
      const response = await CompanyApi.addGoalComment(id, { text });
      if (response?.data?.meta?.code === 1) {
        fetchGoal();
      } else {
        errorToast(response?.data?.meta?.message || 'Failed to add comment');
      }
    } catch (error) {
      errorToast(error.message || 'Failed to add comment');
    } finally {
      setCommentSubmitting(false);
    }
  };

  const handleDelete = async () => {
    try {
      setDeleting(true);
      const response = await CompanyApi.deleteGoal(id);
      if (response?.data?.meta?.code === 1) {
        successToast(response?.data?.meta?.message || 'Goal deleted');
        navigate(`/${slug}/people-plan/goals`);
      } else {
        errorToast(response?.data?.meta?.message || 'Failed to delete goal');
      }
    } catch (error) {
      errorToast(error.message || 'Failed to delete goal');
    } finally {
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const handleSaveDeadline = async () => {
    if (!deadlineDraft) {
      setDeadlineError('Please select a due date.');
      return;
    }
    if (!isFutureDate(deadlineDraft)) {
      setDeadlineError('Please select a future date.');
      return;
    }
    try {
      setSavingDeadline(true);
      const response = await CompanyApi.updateGoal(id, {
        title: goal.title,
        targetMetric: goal.targetMetric,
        endDate: deadlineDraft,
        assignedOwner: (goal.assignedOwner || []).map((o) => o._id || o),
        linkedMilestones: (goal.linkedMilestones || []).map((m) => m._id || m),
        checklist: (goal.checklist || []).map((item) => ({
          text: item.text,
          isCompleted: !!item.isCompleted
        }))
      });
      if (response?.data?.meta?.code === 1) {
        successToast('Deadline updated');
        setEditingDeadline(false);
        fetchGoal();
      } else {
        errorToast(response?.data?.meta?.message || 'Failed to update deadline');
      }
    } catch (error) {
      errorToast(error.message || 'Failed to update deadline');
    } finally {
      setSavingDeadline(false);
    }
  };

  const handleAction = (action) => {
    if (action === 'edit') {
      navigate(`/${slug}/people-plan/goals/edit/${id}`);
    } else if (action === 'delete') {
      setShowDeleteConfirm(true);
    }
  };

  const pages = [
    { name: 'People plan', href: `/${slug}/people-plan`, current: false },
    { name: 'Goals', href: `/${slug}/people-plan/goals`, current: false },
    { name: goal?.title || 'Goal', href: '#', current: true }
  ];

  if (loading && !goal) {
    return (
      <div className="relative lg:mb-[45px]">
        <Loader />
      </div>
    );
  }

  if (!goal) return null;

  // Admins can manage any record; a sub admin only the ones they created.
  const canManage = !isSubAdmin || (goal.createdBy?._id || goal.createdBy) === userData?.id;

  const checklist = goal.checklist || [];
  const completedCount = checklist.filter((item) => item.isCompleted).length;
  const progress = checklist.length > 0 ? Math.round((completedCount / checklist.length) * 100) : 0;

  return (
    <div className="relative lg:mb-[45px]">
      {loading && <Loader />}
      <Helmet>
        <meta charSet="utf-8" />
        <title>{goal.title} - People plan | Shoorah Admin</title>
      </Helmet>
      <Topbar title={goal.title} infoBtn={false} />
      <Breadcrumb pageList={pages} className="lg:mt-8" />

      <div className="mt-6">
        <div className="flex items-center justify-end gap-3 mb-6">
          {canManage && (
            <StandardDropdown
              label="Actions"
              align="right"
              options={[
                {
                  label: 'Edit goal',
                  value: 'edit',
                  icon: <img src={pencilIcon} alt="" className="w-5 h-5 dark:invert" />
                },
                {
                  label: 'Delete goal',
                  value: 'delete',
                  icon: <RecycleBinIcon className="w-5 h-5" />,
                  className: 'text-red-500 hover:text-red-600'
                }
              ]}
              onChange={handleAction}
            />
          )}
        </div>

        <div className="space-y-6">
          <div className="rounded-input border-[1.5px] border-shoorah-borderGray bg-white dark:bg-shoorah-darkBgTabColor dark:border-shoorah-darkBorder p-6">
            <div className="flex items-start gap-3">
              <img src={targetIcon} alt="" className="w-6 h-6 shrink-0 mt-0.5 dark:invert" />
              <div>
                <p className="text-lg font-medium text-gray-700 dark:text-gray-200">{goal.title}</p>
                <p className="text-sm text-gray-400 mt-1">{goal.targetMetric}</p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div className="rounded-input border-[1.5px] border-shoorah-borderGray bg-white dark:bg-shoorah-darkBgTabColor dark:border-shoorah-darkBorder p-6">
              <h3 className="text-base font-medium text-gray-700 dark:text-gray-200 mb-1">
                Progress
              </h3>
              <p className="text-sm text-gray-400 mb-4">
                Progress updates in real-time as you complete the checklist.
              </p>
              <p className="text-xs text-gray-400 text-right mb-1">
                {progress}% until goal reached
              </p>
              <div className="h-1.5 w-full rounded-full bg-shoorah-progressGreenLight dark:bg-shoorah-progressGreen/30 overflow-hidden">
                <div
                  className="h-full rounded-full bg-shoorah-progressGreen"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>

            <div className="rounded-input border-[1.5px] border-shoorah-borderGray bg-white dark:bg-shoorah-darkBgTabColor dark:border-shoorah-darkBorder p-6">
              <div className="flex items-center justify-between mb-1">
                <h3 className="text-base font-medium text-gray-700 dark:text-gray-200">Deadline</h3>
                {canManage && !editingDeadline && (
                  <button
                    type="button"
                    aria-label="Edit deadline"
                    onClick={() => {
                      setDeadlineDraft(goal.endDate ? goal.endDate.substring(0, 10) : '');
                      setDeadlineError('');
                      setEditingDeadline(true);
                    }}
                    className="hover:opacity-70 transition-opacity"
                  >
                    <img src={pencilIcon} alt="" className="w-4 h-4 dark:invert" />
                  </button>
                )}
              </div>
              <p className="text-sm text-gray-400 mb-4">Track your deadline to stay aligned.</p>
              {editingDeadline ? (
                <>
                  <input
                    type="date"
                    autoFocus
                    min={minFutureDateString()}
                    value={deadlineDraft}
                    onChange={(e) => {
                      setDeadlineDraft(e.target.value);
                      setDeadlineError('');
                    }}
                    className={`${FIELD_CLASS} date-field`}
                  />
                  {deadlineError && <p className="text-red-500 text-xs mt-1">{deadlineError}</p>}
                  <div className="flex justify-end gap-2 mt-3">
                    <StandardButton
                      text="Cancel"
                      variant="secondary"
                      onClick={() => setEditingDeadline(false)}
                    />
                    <StandardButton
                      text="Save"
                      disabled={savingDeadline}
                      onClick={handleSaveDeadline}
                    />
                  </div>
                </>
              ) : (
                <div className={`${FIELD_CLASS} flex items-center gap-2`}>
                  <img src={alarmClockIcon} alt="" className="w-5 h-5 shrink-0 dark:invert" />
                  {goal.endDate ? moment(goal.endDate).format('Do MMMM YYYY') : '—'}
                </div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
            <div className="rounded-input border-[1.5px] border-shoorah-borderGray bg-white dark:bg-shoorah-darkBgTabColor dark:border-shoorah-darkBorder p-6 h-full">
              <h3 className="text-base font-medium text-gray-700 dark:text-gray-200 mb-1">
                Goal owners
              </h3>
              <p className="text-sm text-gray-800 mb-4">People responsible for this goal.</p>
              <div className="space-y-3">
                {(goal.assignedOwner || []).length === 0 && (
                  <p className="text-sm text-gray-400">No owners assigned.</p>
                )}
                {(goal.assignedOwner || []).map((owner, index) => (
                  <div key={owner._id || index} className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-shoorah-bodyBackground dark:bg-shoorah-darkBgColor flex items-center justify-center text-xs font-medium text-gray-500 shrink-0">
                      {(owner.name || '?').charAt(0).toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-700 dark:text-gray-200">
                        {owner.name || owner}
                      </p>
                      {getOwnerRoleLabel(owner) && (
                        <p className="text-xs text-gray-400">{getOwnerRoleLabel(owner)}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              {canManage &&
                (addingOwner ? (
                  <div className="mt-3">
                    <Autocomplete
                      placeholder="Search admins..."
                      options={ownerOptions}
                      onSearch={searchOwners}
                      onSelect={handleAddOwner}
                      loading={ownerLoading}
                    />
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => setAddingOwner(true)}
                    className="text-sm text-shoorah-primary font-medium mt-3"
                  >
                    Add owner
                  </button>
                ))}
            </div>

            <div className="rounded-input border-[1.5px] border-shoorah-borderGray bg-white dark:bg-shoorah-darkBgTabColor dark:border-shoorah-darkBorder p-6 h-full">
              <h3 className="text-base font-medium text-gray-700 dark:text-gray-200 mb-1">
                Linked milestone
              </h3>
              <p className="text-sm text-gray-800 mb-4">Tie milestones to your goal.</p>
              <div className="space-y-3">
                {(goal.linkedMilestones || []).length === 0 && (
                  <p className="text-sm text-gray-400">No milestones linked.</p>
                )}
                {(goal.linkedMilestones || []).map((milestone, index) => (
                  <button
                    key={milestone._id || index}
                    type="button"
                    disabled={!milestone._id}
                    onClick={() =>
                      navigate(`/${slug}/people-plan/milestones/view/${milestone._id}`)
                    }
                    className="flex items-start gap-3 text-left w-full disabled:cursor-default"
                  >
                    <img
                      src={checklistIcon}
                      alt=""
                      className="w-5 h-5 shrink-0 mt-0.5 dark:invert"
                    />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-700 dark:text-gray-200">
                        {milestone.title || milestone}
                      </p>
                      {milestone.endDate && (
                        <p className="text-xs text-gray-400 mt-0.5">
                          Due date: {moment(milestone.endDate).format('Do MMMM YYYY')}
                        </p>
                      )}
                    </div>
                  </button>
                ))}
              </div>
              {canManage && (
                <button
                  type="button"
                  onClick={() => navigate(`/${slug}/people-plan/goals/edit/${id}`)}
                  className="text-sm text-shoorah-primary font-medium mt-3"
                >
                  Manage milestones
                </button>
              )}
            </div>
          </div>

          <div className="rounded-input border-[1.5px] border-shoorah-borderGray bg-white dark:bg-shoorah-darkBgTabColor dark:border-shoorah-darkBorder p-6">
            <h3 className="text-base font-medium text-gray-700 dark:text-gray-200 mb-3">
              Checklist
            </h3>
            {checklist.length === 0 && <p className="text-sm text-gray-400">No checklist items.</p>}
            <div className="space-y-2">
              {checklist.map((item) => (
                <div key={item._id} className="flex items-center gap-2">
                  <ChecklistCheckbox
                    checked={!!item.isCompleted}
                    disabled={togglingId === item._id}
                    onToggle={() => handleToggleChecklist(item._id)}
                    label={`Mark "${item.text}" complete`}
                  />
                  <div className={`${FIELD_CLASS} flex-1 flex items-center`}>{item.text}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-input border-[1.5px] border-shoorah-borderGray bg-white dark:bg-shoorah-darkBgTabColor dark:border-shoorah-darkBorder p-6">
            <CommentsPanel
              comments={goal.comments || []}
              onAddComment={handleAddComment}
              submitting={commentSubmitting}
            />
          </div>
        </div>
      </div>

      <ConfirmPopup
        open={showDeleteConfirm}
        setOpen={setShowDeleteConfirm}
        setAccepted={handleDelete}
        handleNo={() => setShowDeleteConfirm(false)}
        message={`Are you sure you want to delete "${goal.title}"? This action cannot be undone.`}
        isLoading={deleting}
      />
    </div>
  );
};

export default ViewGoal;
